import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { HeaderComponent } from "./core/layout/header/header.component";
import { FooterComponent } from './core/layout/footer/footer.component';
import { environment } from '../environments/environment'
import { CommonService } from './core/services/common.service';
import { jwtDecode } from 'jwt-decode';
import { AuthGuardService } from './core/guard/auth-guard/auth-guard.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { clearCodeVerifier, logindetails } from './core/services/login-details';
import { Title } from '@angular/platform-browser';
import { IdleTimeoutService } from './core/services/idle-timeout.service';
import { MatDialog } from '@angular/material/dialog';
import { SignedOutDialogComponent } from './core/layout/signed-out-dialog/signed-out-dialog.component';
import { JwtVerificationService } from './core/services/jwt-verification.service';
import { AuthService } from './core/services/auth.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// SECURITY: Interface for JWT payload with strong typing
interface JwtPayload {
  email?: string;
  given_name?: string;
  family_name?: string;
  roles?: string[];
  preferred_username?: string;
  aud?: string | string[];
  iss?: string;
  tenant?: string;
  exp?: number;
  iat?: number;
}

@Component({
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [CommonModule, RouterOutlet, HeaderComponent, FooterComponent, TranslateModule],
})
export class AppComponent implements OnInit, OnDestroy {
  title = $localize`GCOID TME`;
  public isLightTheme = true;
  codeResponse: string | null = null;
  currentUser: JwtPayload | null = null;
  emailId: string | null = null;
  user_name: string = '';
  last_name: string = '';
  currentUserRole: JwtPayload | null = null;
  roles: string[] | null = null;
  affiliate: string | null = null;
  userName: any;
  private isLoginApiCalled = false;
  private isRefreshingToken = false;
  // Single source of truth for the token-refresh schedule; cleared on destroy and
  // before re-scheduling so timers can't accumulate.
  private refreshIntervalId?: ReturnType<typeof setInterval>;
  private refreshTimeoutId?: ReturnType<typeof setTimeout>;
  private sessionEndedDialogOpen = false;
  // Tracks whether THIS tab is currently signed in, so an already-signed-out tab
  // doesn't show the dialog when another tab's Re-Login clears storage. Declared BEFORE
  // isBootstrapping below since its field initializer depends on this one (class field
  // initializers run in declaration order - referencing a not-yet-initialized field would
  // silently read `undefined` instead of the real value).
  private wasAuthenticated = !!sessionStorage.getItem('AOV_ACCESSTOKEN');
  // Drives a single, continuous, full-screen Toyota-branded loader (see app.component.html/
  // .app-boot-loader) that stays up from initial app bootstrap all the way through the OAuth
  // token exchange, until the FIRST real navigation actually completes (NavigationEnd below).
  // Previously this whole window used ngx-spinner's show()/hide() calls dotted around the
  // NavigationStart handler (plus whatever each destination route's own component showed/hid
  // for its own data loading) - each show/hide toggle is a separate overlay mount/unmount, so
  // the user visibly saw it flicker on and off multiple times before landing on Home. A single
  // boolean driving one persistent overlay for this entire span removes that flicker.
  //
  // Only actually needed for a genuine sign-in transition (a fresh OAuth `?code=` callback,
  // or the brief instant before an unauthenticated tab gets redirected to login) - NOT for a
  // plain reload of an already-authenticated tab. `AOV_ACCESSTOKEN` is written once at login
  // and only cleared on logout/sign-out, so it SURVIVES a reload; its presence here means
  // this is just a reload with an existing session (APP_INITIALIZER already silently
  // re-derives the in-memory tokens from the refresh token before Angular even bootstraps -
  // see CommonService.initializeSessionFromRefreshToken()), not a fresh sign-in. Previously
  // this was hardcoded `true` unconditionally, so the persistent Toyota splash also flashed
  // on every F5 of an already-signed-in page, on top of that page's own loading spinner -
  // redundant and distracting. `logout()` clears sessionStorage before navigating away, so
  // the next load (post sign-out, or a subsequent fresh login) correctly sees no token here
  // and shows the splash again.
  isBootstrapping = !this.wasAuthenticated;

  constructor(private readonly router: Router, private readonly authSrv: AuthGuardService,
    public _service: CommonService, public http: HttpClient,private titleService: Title,
   private readonly idleTimeoutService: IdleTimeoutService,
    private readonly dialog: MatDialog, private readonly jwtVerificationService: JwtVerificationService,
    private readonly authService: AuthService, private readonly translateService: TranslateService
  ) {

    router.events.subscribe(async event => {//NOSONAR
      if (event instanceof NavigationStart) {
        // ------------------------------------------------------------------------------
        // Clear old storage
        if (localStorage.getItem('ADRUM_AGENT_INFO') !== null || localStorage.getItem('ADRUM_CLIENTINFO') !== null
          || localStorage.getItem('accessToken') !== null || localStorage.getItem('firstTimeLogin') !== null || localStorage.getItem('currentUser') !== null) {
          sessionStorage.clear();
          localStorage.clear();
          this.authSrv.clearTokens();
          // event.url is the router's own resolved target path, reliable regardless of
          // whether the address bar reflects it yet - see auth-guard.service.ts's
          // canActivate() for why window.location.href can't be trusted here.
          localStorage.setItem('redirectUrl', event.url);
          const redirectUrl = environment.baseLoginUrl + logindetails.codeChallenge;
          window.location.href = redirectUrl;
        }
        // -----------------------------------------------------------------------------
        if (window.location.href.indexOf('code') !== -1) {
          // Tells the guard a fresh code is being exchanged right now, so its throwaway
          // initial route navigation doesn't get bounced back to Azure login for lacking a
          // token that's only moments away from being set below. (isBootstrapping is
          // already true here - a fresh OAuth `?code=` callback never has a prior
          // AOV_ACCESSTOKEN sitting in sessionStorage, so the boot loader is already
          // visible - no separate spinner.show() needed here.)
          this.authSrv.setLoginExchangeInProgress(true);
          const url = window.location.href.split('?')[1]
          const unauthorized = '/unauthorized';

          // Strip the one-time-use OAuth query params (?code=...&state=...) from the
          // address bar now that we've captured them into `url` above. Angular's hash-based
          // routing only ever touches location.hash, never location.search - so without
          // this, window.location.href keeps containing `code` after ANY subsequent in-app
          // navigation (e.g. our own router.navigate() calls below). That caused every
          // later NavigationStart to re-enter this block and try to exchange the SAME,
          // already-used code again, which is what was producing the extra
          // reload/bounce-back-to-home behavior.
          const cleanUrl = window.location.origin + window.location.pathname + (window.location.hash || '');
          window.history.replaceState(null, '', cleanUrl);

          if (url.indexOf('error') !== -1) {
            // User cancelled/denied consent on Azure's side - this verifier's job is done,
            // don't let the next login attempt reuse it (AppSec Finding #2 assurance work).
            clearCodeVerifier();
            this.authSrv.setLoginExchangeInProgress(false);
            window.location.hash = '#/unauthorized'
            this.router.navigateByUrl(unauthorized)
          }
          else {
            this.codeResponse = url.split('code=')[1].split('&')[0];
            // Azure OAuth authorization codes are single-use - if this exact code was
            // already exchanged in a previous pass (e.g. a lingering/bookmarked `?code=`,
            // or a race before the address bar was cleaned up above), resubmitting it would
            // always be rejected by Azure, clearing storage and bouncing back to login.
            // Skip the doomed re-exchange entirely instead.
            const alreadyConsumedCode = localStorage.getItem('codeResponse');
            if (alreadyConsumedCode && alreadyConsumedCode === this.codeResponse) {
              return;
            }
            localStorage.setItem('codeResponse', this.codeResponse)
            const details: any = {
              'grant_type': environment.grantType,
              'client_id': environment.clientId,
              'code': this.codeResponse,
              'redirect_uri': environment.redirectURI,
              'code_verifier': logindetails.codeVerifier,
            };
            const params = new URLSearchParams(details);
            const body = params.toString();
            await this._service.getTokens(body).then(async (res: any) => {
              // The exchange has concluded (successfully) - clear this before any of the
              // router.navigate() calls below run, so the guard evaluates them against the
              // real, now-populated accessToken instead of the in-flight flag.
              this.authSrv.setLoginExchangeInProgress(false);
              if (res !== null) {
                // The authorization code has been successfully exchanged - this verifier's
                // job is done, don't let a future login attempt reuse it (AppSec Finding #2
                // assurance work).
                clearCodeVerifier();
                this.authSrv.setTokens({
                  idToken: res.id_token,
                  accessToken: res.access_token,
                  refreshToken: res.refresh_token,
                  newIdTokenKey: res.id_token
                });
                // ngOnInit()'s check for this ran before login too (no token yet, so it
                // no-oped) - a fresh login never started idle-activity monitoring
                // otherwise, so the 15-min "Session Timed Out" dialog could never fire.
                // start() itself no-ops if already running, so this can't double-subscribe.
                this.idleTimeoutService.start();
                // ngOnInit()'s call to this already ran before login (with no refresh token
                // yet, so it no-oped) - a fresh login never got its background refresh timer
                // scheduled otherwise. Safe to call again: it clears any previous timer
                // before scheduling, so it can't stack duplicates.
                void this.initializeTokenRefreshWorkflow();
                if (res.id_token && res.access_token) {
                  // Real JWT signature verification (against Azure AD's published JWKS keys) -
                  // NOT just decoding the payload. Only blocks on a DEFINITIVE failure (bad
                  // signature, expired, malformed) - see JwtVerificationService for details.
                  const verification = await this.jwtVerificationService.verifyToken(res.id_token, 'login token exchange (NavigationStart)');
                  if (verification.status === 'invalid') {
                    console.error('[NavigationStart] SECURITY FAILURE: id token failed JWT signature verification - rejecting login');
                    this.authSrv.setIdentityMismatched(true);
                    this.router.navigate([unauthorized]);
                    return;
                  }
                  
                  // SECURITY: Validate JWT audience and issuer claims
                  try {
                    const decodedForValidation: any = jwtDecode(res.id_token);
                    if (!this.validateJwtClaims(decodedForValidation)) {
                      console.error('[NavigationStart] SECURITY FAILURE: JWT claims validation failed (aud/iss mismatch)');
                      this.authSrv.setIdentityMismatched(true);
                      this.router.navigate([unauthorized]);
                      return;
                    }
                  } catch (error) {
                    console.error('[NavigationStart] SECURITY FAILURE: Could not validate JWT claims:', error);
                    this.authSrv.setIdentityMismatched(true);
                    this.router.navigate([unauthorized]);
                    return;
                  }

                  // AuthGuardService.canActivate() blocks the initial '' -> home redirect
                  // and relies ENTIRELY on the explicit router.navigate() call at the end of
                  // this block to be the one-and-only thing that lets the user actually reach
                  // their destination afterward. Wrapping everything below in try/catch/finally
                  // guarantees that navigate() call always fires exactly once no matter what -
                  // an unexpected thrown error here must never leave the user stuck on a blank
                  // page (the guard's blocked attempt never gets a second try on its own).
                  let destinationPath: string | null = null;
                  // Set inside the catch block below when a bad/corrupted login response
                  // (e.g. jwtDecode failing on a malformed access token, which never goes
                  // through signature verification the way the id token above does) has
                  // already triggered a hard window.location redirect back to sign-in - the
                  // finally block below must not ALSO fire a competing router.navigateByUrl
                  // in that case (it would race the real, in-flight page navigation).
                  let hardRedirectTriggered = false;
                  try {
                    const decodedIDToken: any = jwtDecode(res.id_token);
                    const decodedACCESSToken: any = jwtDecode(res.access_token);
                    sessionStorage.setItem('AOV_IDTOKEN', JSON.stringify(decodedIDToken));
                    sessionStorage.setItem('AOV_ACCESSTOKEN', JSON.stringify(decodedACCESSToken));
                    this.currentUser = decodedACCESSToken;
                    this.currentUserRole = decodedIDToken;
                    // REQUIRED claims - the rest of the app cannot function meaningfully
                    // without these (email drives every API call's UserId/filter, roles
                    // drives every authorization/affiliate decision). Read them optionally-
                    // chained (never throw on a missing claim here) and explicitly guard
                    // below instead of letting e.g. `emailId.toLowerCase()` on undefined
                    // blow up and silently abort this whole login flow via the outer catch.
                    this.emailId = this.currentUser?.['email'] ?? null;
                    this.roles = this.currentUserRole?.['roles'] ?? null;
                    if (!this.emailId || !Array.isArray(this.roles) || this.roles.length === 0) {
                      // Clean, explicit failure instead of an uncaught throw further down -
                      // treat exactly like the identity-mismatch/invalid-signature checks
                      // above: block navigation and force the user back through login rather
                      // than proceeding with a half-populated session.
                      console.error('[NavigationStart] Login token is missing required claims (email/roles) - forcing re-login instead of proceeding.');
                      this.authSrv.setIdentityMismatched(true);
                      destinationPath = unauthorized;
                      return;
                    }
                    // Single centralized derivation (AuthService) for both the affiliate code
                    // and the company badge value - previously these were computed via two
                    // separate, subtly different code paths here (and yet a THIRD, divergent
                    // path in header.component.ts's getaffilate()).
                    this.affiliate = this.authService.resolveAffiliateCode(this.roles);
                    this.authSrv.setCompanyAfl(this.affiliate);
                    // Stored as real JSON (not an array coerced to a comma-separated string)
                    // so every reader can check role membership reliably via array `.includes(...)`.
                    this._service.setStoredRoles(this.roles);
                    localStorage.setItem('Affiliate', this.affiliate);
                    // OPTIONAL (display-only) claims - a profile missing given_name/
                    // family_name (e.g. no Business Title-style optional field populated)
                    // must still sign in; the header's userName getter already renders each
                    // half blank via `?? ''`, so falling back to '' here just shows a blank
                    // name segment instead of throwing/blocking login.
                    this.user_name = this.currentUser?.['given_name'] ?? '';
                    this.last_name = this.currentUser?.['family_name'] ?? '';
                    localStorage.setItem('emailId', this.emailId.toLowerCase())
                    localStorage.setItem('username', this.user_name)
                    localStorage.setItem('lastname', this.last_name);

                    // Defense-in-depth identity check: confirm the id token we're about to
                    // use as a bearer credential actually belongs to the signed-in user. On
                    // a CONFIRMED mismatch, stop here - stay on /unauthorized and skip
                    // onGetRole()/getAffiliatesList and the redirectUrl navigation entirely,
                    // so nothing later (e.g. its reload-on-success) can undo the redirect.
                    if (this.isIdTokenIdentityMismatch(this.authSrv.getNewIdTokenKey() || '')) {
                      console.error('[NavigationStart] id token identity does not match the signed-in user - redirecting to /unauthorized.');
                      // Block ALL other navigation (including header.component.ts's own,
                      // independent role/affiliate fetch flow) at the router-guard level so
                      // nothing can navigate the user away from /unauthorized afterward.
                      this.authSrv.setIdentityMismatched(true);
                      destinationPath = unauthorized;
                      return;
                    }

                    this.onGetRole();
                    // HeaderComponent reads Affiliate/roles at CONSTRUCTION time - on a
                    // fresh (first-ever) login it was already constructed before this login
                    // flow finished writing them to localStorage, so its menu/affiliate never
                    // showed up until a full reload rebuilt it from scratch. Now that the
                    // reload is gone, tell it explicitly to re-fetch in-place instead.
                    this._service.notifyAuthReady();
                    // redirectUrl is a clean path captured from the guard's own
                    // RouterStateSnapshot (e.g. '/admin'), not a full URL needing hash parsing.
                    const redirectURL = localStorage.getItem('redirectUrl');
                    destinationPath = (redirectURL && this.affiliate) ? redirectURL : unauthorized;
                  } catch (error) {
                    // A bad/corrupted login response must recover cleanly, not freeze or
                    // silently proceed with a half-populated session - clear storage and
                    // send the user back to sign-in, the same recovery pattern already used
                    // by the sibling getTokens() failure handler just below and by
                    // initializeTokenRefreshWorkflow()'s own catch.
                    console.error('[NavigationStart] Unexpected error while finishing login - clearing session and returning to sign-in instead of proceeding with corrupted data.', error);
                    hardRedirectTriggered = true;
                    sessionStorage.clear();
                    localStorage.clear();
                    this.authSrv.clearTokens();
                    window.location.href = environment.redirectURI;
                  } finally {
                    // Exactly ONE navigate() call, no matter which path above set
                    // destinationPath (success, identity mismatch, or unexpected error) -
                    // never zero (would strand the user) and never more than one (would
                    // race with itself and visibly look like the app "loading" twice).
                    // Falls back to /home if the resolved path is ever empty/invalid instead
                    // of leaving the user stranded on a blank screen. Skipped entirely when
                    // the catch above already triggered a hard redirect to sign-in.
                    if (!hardRedirectTriggered) {
                      this.router.navigateByUrl(destinationPath || '/home').catch(() => {
                        this.router.navigateByUrl('/home');
                      });
                    }
                  }
                }
              }
            }, (_err: any) => {
              this.authSrv.setLoginExchangeInProgress(false);
              setTimeout(() => {
                sessionStorage.clear();
                localStorage.clear();
                this.authSrv.clearTokens();
                window.location.href = environment.redirectURI;
              }, 2000)
            })
          }
        }
        else {
          // Every OTHER (non-OAuth-code) NavigationStart is a normal in-app route change -
          // e.g. clicking a nav link, or switching back to an already-open tab and then
          // navigating within it. The id token is otherwise only re-verified at login and
          // once per app bootstrap/reload (see initializeTokenRefreshWorkflow) - without a
          // check here too, a token tampered with mid-session (after either of those
          // checkpoints already ran) would never be caught until the next full reload. The
          // route would activate anyway (AuthGuardService's canActivate does not itself
          // check the token's signature), the new component would try to load its data with
          // the bad token, and depending on how that component handles the resulting error
          // it can end up rendering blank instead of ever reaching /unauthorized.
          const idToken = this.authSrv.getIdToken();
          if (idToken && !this.authSrv.isIdentityMismatched()) {
            const verification = await this.jwtVerificationService.verifyToken(idToken, 'in-app navigation (NavigationStart)');
            if (verification.status === 'invalid') {
              // console.error('[NavigationStart] id token failed JWT signature verification during in-app navigation - redirecting to /unauthorized.');
              this.authSrv.setIdentityMismatched(true);
              this.router.navigate(['/unauthorized']);
              return;
            }
            // SECURITY (AppSec High Finding #7): re-run the fail-closed aud/iss check on
            // every in-app navigation too, not just at login/bootstrap - closes the same
            // window as the JWT signature re-check just above.
            try {
              const decodedForValidation: any = jwtDecode(idToken);
              if (!this.validateJwtClaims(decodedForValidation)) {
                console.error('[NavigationStart] SECURITY FAILURE: JWT claims validation failed (aud/iss mismatch) during in-app navigation - redirecting to /unauthorized.');
                this.authSrv.setIdentityMismatched(true);
                this.router.navigate(['/unauthorized']);
                return;
              }
            } catch (error) {
              console.error('[NavigationStart] SECURITY FAILURE: Could not validate JWT claims during in-app navigation:', error);
              this.authSrv.setIdentityMismatched(true);
              this.router.navigate(['/unauthorized']);
              return;
            }
          }
        }
        const role = this.authSrv.getRole();
        const url = event.url;
        const unauthorized = '/unauthorized';
        // Match the section root AND any of its nested routes (e.g. bookmarked
        // /approvals/view-questions/:category/:affiliate/:id), not just an exact path.
        const isAdminRoute = url === '/admin' || url.startsWith('/admin/');
        const isApprovalsRoute = url === '/approvals' || url.startsWith('/approvals/');
        if (role === 'Approver' && isAdminRoute) {//NOSONAR
          router.navigate(['/home']);
        }
        else if ((role === 'User' && isAdminRoute) || (role === 'User' && isApprovalsRoute)) {
          router.navigate(['/home']);
        }
        else if ((role === 'ApproverAdmin') && isAdminRoute) {
          router.navigate(['/home']);
        }
        else if (( role === 'Unauthorized') && ( isAdminRoute || isApprovalsRoute || url === '/home' || url === '/')) {
          router.navigate([unauthorized]);
        }
      }
      // Run token-dependent work once per navigation, not on every router event.
      if (event instanceof NavigationEnd) {
        // The FIRST real navigation to actually complete (guard passed, component
        // activated) - this is what turns off the continuous boot loader, whether it's
        // Home, Unauthorized, or anything else. A blocked/cancelled navigation (e.g. the
        // guard rejecting '' -> home while a fresh login is still in flight) fires
        // NavigationCancel, never NavigationEnd, so the loader correctly stays up through
        // the whole token exchange with no flicker until this point.
        this.isBootstrapping = false;
        if (environment.title) {
          this.setTitle(environment.title);
        }
        this._service.setaffiliate(localStorage.getItem('Affiliate'));
        this._service.setlangugae(localStorage.getItem('langCode'));
        const rawAccessToken = sessionStorage.getItem('AOV_ACCESSTOKEN');
        this.currentUser = rawAccessToken ? JSON.parse(rawAccessToken) : null;
        this.affiliate = localStorage.getItem('Affiliate');
        // Only touch token-derived fields when a decoded token actually exists,
        // so pre-auth and mid-session token loss don't throw or freeze the header.
        if (this.currentUser) {
          this.wasAuthenticated = true;
          this.userName = this.currentUser['given_name'] + ' ' + this.currentUser['family_name'];
          this.emailId = this.currentUser['email'] ?? null;
          if (!this.isLoginApiCalled && this.affiliate && this.emailId && (sessionStorage.getItem('loginApiCalled') !== 'true')) { // Check if the API call has already been made
            this.isLoginApiCalled = true; // Set the flag to true to prevent further calls
            this._service.loginApi(this.userName, this.emailId.toLowerCase(), this.affiliate).subscribe((res: any) => {
              //console.info('Login API response:', res);
              sessionStorage.setItem('loginApiCalled', 'true');
            });
          }
        }
      }
    })
  }

  ngOnInit(): void {
    void this.initializeTokenRefreshWorkflow();
    // Safe to check synchronously - the APP_INITIALIZER (see app.config.ts) guarantees the
    // in-memory access token is already populated before this component is constructed,
    // even right after a reload. Don't gate this on initializeTokenRefreshWorkflow()
    // finishing - that chain also awaits JWT signature verification, and if that's slow or
    // unreachable the idle timer would never start.
    if (this.authSrv.getAccessToken()) {
      this.idleTimeoutService.start();
    }
  }

  ngOnDestroy(): void {
    this.clearRefreshTimers();
  }

  private clearRefreshTimers(): void {
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = undefined;
    }
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = undefined;
    }
  }


  // Cross-tab sign-out sync. `logout()`/every auth-failure path clears `localStorage`
  // (see header.component.ts, auth-guard.service.ts, idle-timeout.service.ts, etc.) -
  // `localStorage.clear()` fires a real `storage` event (key: null) in every OTHER
  // same-origin tab/window, regardless of how that tab was opened (unlike sessionStorage,
  // which never fires cross-tab at all for genuinely independent tabs - it's scoped per
  // tab/window, not shared, even for two tabs of the exact same origin). Previously this
  // handler ALSO required `!sessionStorage.getItem('AOV_ACCESSTOKEN')` to be true here -
  // but that reads THIS tab's own, completely unrelated sessionStorage, which another
  // tab's sign-out can never actually touch - so for two genuinely independent tabs (the
  // common case: opened separately, not via window.open from one another), that extra
  // check could never be satisfied and this listener silently never fired. Removed it -
  // the `event.key === null` (or the narrower 'AOV_ACCESSTOKEN' check, kept for the rare
  // window.open-shared-storage case) from the real cross-tab `storage` event is already
  // the correct, sufficient signal on its own.
  @HostListener('window:storage', ['$event'])
  onStorageChange(event: StorageEvent): void {
    const tokenCleared = event.key === 'AOV_ACCESSTOKEN' || event.key === null;
    // Only react if THIS tab believed it was signed in; ignore the clear otherwise (e.g.
    // this tab is already sitting on the login page).
    if (tokenCleared && this.wasAuthenticated) {
      this.wasAuthenticated = false;
      // header.component.ts's logout() writes this marker (AFTER its own localStorage.
      // clear()) only when the user explicitly clicked Sign Out - every OTHER path that
      // clears storage (idle timeout, token-refresh failure, corrupted login response,
      // interceptor 401, identity mismatch) never sets it. By the time THIS tab's handler
      // runs for the `key === null` clear event, the other tab's synchronous script has
      // already finished (including any setItem() right after its own clear()), so this
      // read reliably reflects which kind of sign-out actually happened.
      const wasManualSignOut = !!localStorage.getItem('manualSignOut');
      this.showSessionEndedDialog(wasManualSignOut);
    }
  }

  private showSessionEndedDialog(wasManualSignOut: boolean): void {
    if (this.sessionEndedDialogOpen) {
      return;
    }
    this.sessionEndedDialogOpen = true;
    if (wasManualSignOut) {
      // Intentional, user-clicked sign-out in another tab - every other tab must sign out
      // automatically and instantly here too, with no action required (no clicking
      // "Re-Login", no manually switching to/reloading that tab). Storage was already
      // cleared by the tab that signed out; redirect straight to the actual sign-out
      // endpoint (NOT environment.baseLoginUrl, the authorize/login endpoint) so this
      // tab's browser session is genuinely ended at the IdP too, not just locally -
      // matches header.component.ts's own logout() redirect target.
      window.location.href = environment.logoutUrl;
      return;
    }
    // Any OTHER reason storage got cleared (idle timeout, a token-refresh failure,
    // corrupted login response, interceptor 401, identity mismatch) - old behavior:
    // show the dialog and let the user decide when to re-login, don't force an instant
    // redirect out from under them (e.g. mid-typing something) for something that wasn't
    // a deliberate action on their part.
    const dialogRef = this.dialog.open(SignedOutDialogComponent, {
      disableClose: true,
      width: '420px',
      panelClass: 'session-timeout-dialog-panel'
    });
    dialogRef.afterClosed().subscribe(() => {
      // Storage was already cleared by the tab that originally signed out; just redirect
      // so we don't re-broadcast a clear event to other tabs.
      window.location.href = environment.baseLoginUrl + logindetails.codeChallenge;
    });
  }

  private async initializeTokenRefreshWorkflow(): Promise<void> {
    const refreshTokenValue = this.authSrv.getRefreshToken();
    if (!refreshTokenValue) {
      return;
    }

    // Memory never survives a fresh page load, so on every bootstrap (including a plain
    // F5, not just right after login) id_token/access_token start out empty here and must
    // be silently re-derived from the persisted refresh token before anything else runs.
    let token = this.authSrv.getAccessToken();
    let idToken = this.authSrv.getIdToken();

    if (!token || !idToken) {
      try {
        const res: any = await this._service.getTokens(new URLSearchParams({
          'grant_type': 'refresh_token',
          'client_id': environment.clientId,
          'refresh_token': refreshTokenValue
        }).toString());

        if (!res?.id_token || !res?.access_token || !res?.refresh_token) {
          sessionStorage.clear();
          localStorage.clear();
          this.authSrv.clearTokens();
          window.location.href = environment.redirectURI;
          return;
        }

        this.authSrv.setTokens({
          idToken: res.id_token,
          accessToken: res.access_token,
          refreshToken: res.refresh_token,
          newIdTokenKey: res.id_token
        });
        token = res.access_token;
        idToken = res.id_token;
      } catch (error) {
        console.error('Error silently refreshing tokens on startup:', error);
        sessionStorage.clear();
        localStorage.clear();
        this.authSrv.clearTokens();
        window.location.href = environment.redirectURI;
        return;
      }
    }

    if (token && idToken) {
      try {
        const decodedToken: any = jwtDecode(token);
        const decodedIdToken: any = jwtDecode(idToken);
        const now = Date.now();

        const tokenExpiry = decodedToken.exp * 1000;
        const idTokenExpiry = decodedIdToken.exp * 1000;

        // If BOTH tokens expired, redirect to login immediately
        if (now > tokenExpiry || now > idTokenExpiry) {
          sessionStorage.clear();
          localStorage.clear();
          this.authSrv.clearTokens();
          window.location.href = environment.redirectURI;
          return;
        }

        // Real JWT signature verification against Azure AD's JWKS, re-run on EVERY app
        // bootstrap/reload (not just right after login) so a tampered/forged token sitting
        // in storage can't silently be trusted just because it decodes with valid-looking
        // claims. Only blocks on a DEFINITIVE failure - see JwtVerificationService.
        const verification = await this.jwtVerificationService.verifyToken(idToken, 'app bootstrap (initializeTokenRefreshWorkflow)');
        // console.log(verification)
        if (verification.status === 'invalid') {
          console.error('[initializeTokenRefreshWorkflow] SECURITY FAILURE: id token failed JWT signature verification - redirecting to /unauthorized.');
          this.authSrv.setIdentityMismatched(true);
          this.router.navigate(['/unauthorized']);
          return;
        }

        // SECURITY (AppSec High Finding #7 - "Audience and issuer validation fails open"):
        // the same fail-closed aud/iss check already run at login is re-run on EVERY app
        // bootstrap/reload too, not just once at the original login token exchange - a
        // token whose aud/iss no longer matches this app (e.g. swapped for one issued to a
        // different Azure AD app/tenant) must not silently keep being trusted just because
        // it happens to still carry a valid signature from Microsoft.
        if (!this.validateJwtClaims(decodedIdToken)) {
          console.error('[initializeTokenRefreshWorkflow] SECURITY FAILURE: JWT claims validation failed (aud/iss mismatch) - redirecting to /unauthorized.');
          this.authSrv.setIdentityMismatched(true);
          this.router.navigate(['/unauthorized']);
          return;
        }

        // Re-validate the id token's identity on EVERY app bootstrap (not just right after
        // the OAuth code exchange). This is what makes the mismatch redirect survive a
        // plain page reload - AuthGuardService's in-memory flag alone would otherwise reset
        // to false on reload, since a fresh reload never re-runs the exchange-time check.
        if (!this.emailId && decodedToken?.email) {
          this.emailId = decodedToken.email;
        }
        if (this.isIdTokenIdentityMismatch(idToken)) {
          console.error('[initializeTokenRefreshWorkflow] id token identity does not match the signed-in user - redirecting to /unauthorized.');
          this.authSrv.setIdentityMismatched(true);
          this.router.navigate(['/unauthorized']);
          return;
        }

        // Both the signature verification and identity check passed, so this is a
        // genuinely valid, trusted token. If the user is currently sitting on
        // /unauthorized (e.g. left over from an earlier failed check, or a stale tab
        // reload), move them forward to /home instead of leaving them stranded there.
        if (this.router.url.startsWith('/unauthorized')) {
          this.authSrv.setIdentityMismatched(false);
          this.router.navigate(['/home']);
        }

        // Try to refresh if within 10 minutes of expiry
        const timeUntilExpiry = Math.min(tokenExpiry, idTokenExpiry) - now;
        if (timeUntilExpiry < 10 * 60 * 1000) {
          void this.refreshToken();
        }

        const timeLeft = Math.min(tokenExpiry, idTokenExpiry) - now;
        // Clear any previously scheduled timers so re-entry can't stack duplicates.
        this.clearRefreshTimers();
        //console.log('[refresh-timer] Scheduler CREATED. setTimeout fires in', Math.round(timeLeft / 1000), 'sec (near expiry); setInterval every 15 min.');
        this.refreshTimeoutId = setTimeout(() => {
          //console.log('[refresh-timer] setTimeout TICK (near expiry) -> refreshToken()');
          void this.refreshToken();
        }, timeLeft);
        this.refreshIntervalId = setInterval(() => {
          //console.log('[refresh-timer] setInterval TICK (15 min) -> refreshToken()');
          void this.refreshToken();
        }, 900000); // Every 15 mins
      } catch (error) {
        console.error('Error validating token on startup:', error);
        sessionStorage.clear();
        localStorage.clear();
        this.authSrv.clearTokens();
        window.location.href = environment.redirectURI;
      }
    }
  }
    
    setTitle(newTitle: string) {
    this.titleService.setTitle(newTitle);
  }




  onGetRole() {

    const token: any = this.authSrv.getAccessToken() ?? ''
    const newId_token_key: any = this.authSrv.getNewIdTokenKey() ?? ''
    const isIEOrEdge = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
    let httpOptions: any;
    const contentType = 'application/x-www-form-urlencoded; charset=UTF-8';
    if (isIEOrEdge) {
      httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': contentType,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Authorization': newId_token_key
        })
      };
    } else {
      httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': contentType,
          'Authorization': newId_token_key
        })
      };
    }
    const url = environment.adminStack + 'getAffiliatesList/{proxy+}' + '?affiliate=' + this.affiliate + '&lang=' + 'null' + '&defaultlang=' + 'null';
    this.http.get(url, httpOptions).subscribe((res: any) => {
      if (res.payload[0] !== null) {
        let affilateList = res.payload;
        // Falls back to AuthService.resolveAffiliateCode() (prefer a role containing
        // 'User', not just roles[0], AND run through the same TMNA->TMN/etc mapping) when
        // no Affiliate is stored yet (e.g. first login ever). Previously this used
        // `this.roles[0]` directly with NO mapping - Azure AD does NOT guarantee
        // role-claim ordering, so for a user whose first role happened to be something
        // other than their real affiliate (e.g. "GLOBAL-User", "DEV-Admin"), the lookup
        // below found nothing, `affilateList` became undefined, and the very next line
        // (`affilateList.default_lang`) threw - silently skipping langCode/AppName/
        // defaultlang setup entirely and leaving every other API call sending `lang=null`.
        const fallbackAffiliateCode = this.authService.resolveAffiliateCode(this.roles);
        affilateList = affilateList.find((item: any) => item.affiliate_code === ((localStorage.getItem('Affiliate') !== undefined) && (localStorage.getItem('Affiliate') !== null) ? localStorage.getItem('Affiliate') : fallbackAffiliateCode));
        if (!affilateList) {
          console.error('[onGetRole] No matching affiliate found for this user\'s roles - falling back to "en" instead of leaving lang/AppName unset.', { roles: this.roles, fallbackAffiliateCode });
          localStorage.setItem("langCode", 'en')
          localStorage.setItem("defaultlang", 'en')
          return;
        }
        localStorage.setItem("langCode", affilateList.default_lang)
        this._service.setlangugae(localStorage.getItem('langCode'));
        localStorage.setItem("AppName", affilateList.app_name)
        localStorage.setItem("defaultlang", affilateList.default_lang)
        // Apply the resolved language in-place instead of a full page reload - a reload
        // here re-bootstraps the whole app and re-runs the initial NavigationStart, which
        // (combined with any lingering `?code=` in the URL) could re-submit an
        // already-consumed, single-use authorization code and get Azure to reject it,
        // clearing storage and bouncing the user back to login. HeaderComponent's
        // title/AppName are plain getters reading localStorage directly, so they pick up
        // the values written above on the next change-detection cycle with no extra code.
        this.translateService.use(affilateList.default_lang);
      }
    }, (err: any) => {
      localStorage.setItem("langCode", 'en')
      localStorage.setItem("defaultlang", 'en')
    })
  }
  async refreshToken() {
    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshingToken) {
      return;
    }

    try {
      this.isRefreshingToken = true;
      
      const refreshTokenValue = this.authSrv.getRefreshToken();
      const token = this.authSrv.getAccessToken();
      
      if (!refreshTokenValue || !token) {
        console.error('Required tokens not found. Redirecting to login.');
        this.handleTokenRefreshFailure();
        return;
      }

      try {
        const tokenDecode: any = jwtDecode(token);
        const currentTime = Date.now();
        const timeUntilExpiry = (tokenDecode.exp * 1000) - currentTime;
        
        // Only refresh if less than 60 minutes remain
        if (timeUntilExpiry > 60 * 60 * 1000) {
          return;
        }
      } catch (error) {
        console.error('Invalid token format:', error);
        this.handleTokenRefreshFailure();
        return;
      }

      const params = new URLSearchParams({
        'grant_type': 'refresh_token',
        'client_id': environment.clientId,
        'refresh_token': refreshTokenValue
      });

      // A real refresh-token exchange is about to happen WHILE the user is already using
      // the app (this method runs on a background interval/near-expiry timer, well after
      // the initial bootstrap splash already cleared at the first NavigationEnd) - show the
      // same full-screen splash for this network round trip too, per the requirement that
      // it should reappear for "any refresh token" exchange, not just initial login/logout.
      this.isBootstrapping = true;
      try {
        const res: any = await this._service.getTokens(params.toString());
        
        if (res?.id_token && res?.access_token && res?.refresh_token) {
          this.authSrv.setTokens({
            idToken: res.id_token,
            accessToken: res.access_token,
            refreshToken: res.refresh_token,
            newIdTokenKey: res.id_token
          });
        } else {
          console.error('Invalid token response:', res);
          this.handleTokenRefreshFailure();
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        this.handleTokenRefreshFailure();
      }
    } finally {
      this.isRefreshingToken = false;
      // Guaranteed to run whether the refresh above succeeded, failed, or was never even
      // attempted this tick (e.g. the `timeUntilExpiry > 60 minutes` early return) -
      // harmless in that last case since isBootstrapping is already false by then.
      this.isBootstrapping = false;
    }
  }

  private handleTokenRefreshFailure() {
    setTimeout(() => {
      sessionStorage.clear();
      localStorage.clear();
      this.authSrv.clearTokens();
      window.location.href = environment.redirectURI;
    }, 2000);
  }

  // Extracts a usable identity claim from a decoded JWT payload. Azure AD doesn't always
  // populate `email` on every token - depending on the app registration's optional claims
  // configuration, the user's identity may instead be carried in `preferred_username`,
  // `upn`, or `unique_name`. Checking all of them keeps identity verification reliable
  // across tenants/configurations instead of silently breaking whenever `email` is absent.
  private extractIdentityClaim(decoded: any): string | null {
    const raw = decoded?.['Work Email'] ?? decoded?.email ?? decoded?.preferred_username ?? decoded?.upn ?? decoded?.unique_name ?? null;
    return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim().toLowerCase() : null;
  }

  // Confirms the id token about to be used as a bearer credential actually identifies the
  // signed-in user (per the access token's email, captured earlier in this.emailId).
  //
  // This is intentionally FAIL-OPEN: it only reports a mismatch when BOTH identities are
  // present and they genuinely disagree. If the id token can't be decoded, or neither
  // token exposes a recognizable identity claim, it does NOT report a mismatch - a
  // validation we can't reliably perform must never itself take down login for every user.
  private isIdTokenIdentityMismatch(newIdTokenKey: string): boolean {
    if (!newIdTokenKey) {
      return false;
    }

    let idTokenIdentity: string | null = null;
    try {
      idTokenIdentity = this.extractIdentityClaim(jwtDecode(newIdTokenKey));
    } catch (error) {
      console.warn('[onGetRole] Could not decode id token to verify identity; skipping the check.', error);
      return false;
    }

    const signedInIdentity = this.emailId ? String(this.emailId).trim().toLowerCase() : null;
    if (!idTokenIdentity || !signedInIdentity) {
      return false;
    }

    return idTokenIdentity !== signedInIdentity;
  }

  // SECURITY: Validate JWT audience (aud) and issuer (iss) claims.
  //
  // REVERTED 2026-09-30 (see jwt-verification.service.ts's top-of-file incident note for the
  // full story): this briefly hard-FAILED (returned false, which blocks login/bootstrap) on
  // any exact aud/iss mismatch, using a GUESSED exact issuer/audience format that was never
  // confirmed against a real production token - caused a login/reload loop for real users.
  // Now delegates to the SAME hasExactAudience()/hasExactIssuer() helpers purely for
  // diagnostic logging, but a mismatch no longer blocks - only a token with NO aud/iss claim
  // at all (never legitimate, any format) is treated as a hard failure.
  private validateJwtClaims(payload: any): boolean {
    if (!payload?.aud) {
      console.error('[validateJwtClaims] SECURITY: token has no aud claim at all', { received: payload?.aud });
      return false;
    }
    if (!payload?.iss) {
      console.error('[validateJwtClaims] SECURITY: token has no iss claim at all', { received: payload?.iss });
      return false;
    }

    if (!this.jwtVerificationService.hasExactAudience(payload)) {
      console.warn('[validateJwtClaims] aud claim did not match the assumed exact client ID format (NOT blocking - see jwt-verification.service.ts incident note)', {
        received: payload?.aud,
        expected: this.jwtVerificationService.getExpectedAudiences()
      });
    }

    if (!this.jwtVerificationService.hasExactIssuer(payload)) {
      console.warn('[validateJwtClaims] iss claim did not match the assumed exact tenant format (NOT blocking - see jwt-verification.service.ts incident note)', {
        received: payload?.iss,
        expected: this.jwtVerificationService.getExpectedIssuers()
      });
    }

    return true;
  }
}

